import {
    IonCol,
    IonItem,
    IonLabel,
    IonList,
    IonRow,
    IonSearchbar,
    isPlatform,
} from "@ionic/react";
import moment from "moment";
import React, {useEffect, useState} from "react";
import { Table } from "reactstrap";
import { EventFilter } from "../../../API";
import { Event, EventAuditor, EventAuditorFilter } from "../../../models";
import { getEventAuditorById, getEventAuditorsWithFilterByEventId } from "../../../utilities/eventAuditor/EventAuditor";
import { sortEventAuditorsByName } from "../../../utilities/eventAuditor/SortEventAuditors";
import { getEventFiltersByEventId } from "../../../utilities/eventFilter/EventFilter";
import { isWindows } from "../../../utilities/platform/Platform";
import ErrorAlert from "../../Errors/ErrorAlert";
import EventAuditorModal from "../../EventAuditor/EventAuditorModal";
import Spinner from "../../Spinners/Spinner";
import PhoneInput from "react-phone-number-input";

interface _Props {
    event: Event
    eventAuditor?: EventAuditor
}

const EditEventAuditorsFullTable: React.FC<_Props> = ({event, eventAuditor}) => {    

    const [eventAuditors, setEventAuditors] = useState<EventAuditor[] | null | undefined>();
    const [filteredEventAuditors, setFilteredEventAuditors] = useState<EventAuditor[] | null | undefined>();
    const [searchText, setSearchText] = useState('');
    const [selectedEventAuditor, setSelectedEventAuditor] = useState<EventAuditor | null | undefined>();
    const [eventFilters, setEventFilters] = useState<EventFilter[] | null | undefined>();
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    async function getEventAuditors(event: Event, eventFilters: EventFilter[]) {
        setIsLoading(true);
        const queryResult = await getEventAuditorsWithFilterByEventId(event.id);
        if (queryResult.isSuccess) {
            let eventAuditors: EventAuditor[] = sortEventAuditorsByName(queryResult.result) || queryResult.result;
            let updatedAuditors: EventAuditor[] = [];
            if (eventAuditors && eventAuditors.length > 0) {
                for (let i = 0; i < eventAuditors.length; i++) {
                    const currentEventAuditor = eventAuditors[i];
                    const filterResult: any = currentEventAuditor.filters;
                    const filters = filterResult?.items;
                    if (eventFilters) {
                        if (filters && filters.length) {
                            let sorted: (EventAuditorFilter | null)[] | null = [];
                            for (let j = 0; j < eventFilters.length; j++) {
                                const eventFilter = eventFilters[j];
                                let found = false;
                                for (let k = 0; k < filters.length; k++) {
                                    const currentFilter = filters[k];
                                    if (currentFilter && currentFilter.filterId === eventFilter.id) {
                                        sorted.push(currentFilter);
                                        found = true;
                                        break;
                                    } 
                                }
                                if (!found) sorted.push(null);
                            }
                            const updatedEventAuditor = {
                                ...currentEventAuditor,
                                filters: sorted
                            };
                            updatedAuditors.push(updatedEventAuditor);
                        } else {
                            updatedAuditors.push(currentEventAuditor);
                        }
                    }  else {
                        updatedAuditors.push(currentEventAuditor);
                    }
                }
            }
            setEventAuditors(updatedAuditors);
            setFilteredEventAuditors(updatedAuditors);
            setIsLoading(false);
        } else {
            setIsLoading(false);
        }
    }

    async function getEventFilters(event: Event) {
        const queryResult = await getEventFiltersByEventId(event.id);
        if (queryResult.isSuccess) {
            const eventFilters: EventFilter[] = queryResult.result;
            const sorted = eventFilters.sort((a, b) => (a.name || "").localeCompare((b.name || "")));
            setEventFilters(sorted);
            getEventAuditors(event, sorted);
        }
    }

    useEffect(() => {
        if (event) {
            getEventFilters(event);
        }
    }, [event]);

    useEffect(() => {
        if (eventAuditor) {
            if (eventAuditors) {
                const updated = eventAuditors.concat(eventAuditor);
                const sorted = updated.sort((a, b) => (a.name || "").localeCompare((b.name || "")));
                setEventAuditors(sorted);
            } else {
                setEventAuditors([eventAuditor]);
            }
        }
    }, [eventAuditor]);

    const handleSelectedSubscriber = (eventAuditor: EventAuditor) => {
        setSelectedEventAuditor(eventAuditor);
        setShowModal(true);
    }

    const handleEventAuditorChange = async (action: ("create" | "update" | "delete" | "close"), eventAuditor: EventAuditor) => {
        setIsLoading(true);
        setShowModal(false);
        // Check if the user simply closed the modal without completing a main action
        if (action !== "close") {
            // If a main action was completed, then update the list of event auditors accordingly
            let updatedEventAuditorArray: EventAuditor[] = [eventAuditor];
            let updatedFilteredEventAuditorArray: EventAuditor[] = [eventAuditor];
            if (action === "create") {
                if (eventAuditors) updatedEventAuditorArray = updatedEventAuditorArray.concat(eventAuditors);
                if (filteredEventAuditors) updatedFilteredEventAuditorArray = updatedFilteredEventAuditorArray.concat(filteredEventAuditors);
            } else if (action === "update") {
                if (eventAuditors) {
                    let updatedEventAuditor: EventAuditor = eventAuditor;
                    if (eventFilters) {
                        const eventAuditorQueryResult = await getEventAuditorById(eventAuditor.id);
                        if (eventAuditorQueryResult.isSuccess) {
                            const queriedEventAuditor: EventAuditor = eventAuditorQueryResult.result;
                            if (queriedEventAuditor.filters) {
                                const filterItems: any = queriedEventAuditor.filters;
                                const filters: EventAuditorFilter[] = filterItems.items;
                                let sorted: EventAuditorFilter[] = [];
                                for (let j = 0; j < eventFilters.length; j++) {
                                    const eventFilter = eventFilters[j];
                                    const findResult = filters.find(eventAuditorFilter => eventAuditorFilter.filterId === eventFilter.id);
                                    if (findResult) {
                                        sorted.push(findResult);
                                    } else {
                                        const blankEventAuditorFilter: EventAuditorFilter = {
                                            id: "1",
                                            options: [],
                                            createdOn: moment(new Date()).format("MM/DD/YY"),
                                            updatedOn: moment(new Date()).format("MM/DD/YY")
                                        };
                                        sorted.push(blankEventAuditorFilter);
                                    }
                                }
                                updatedEventAuditor = {
                                    ...eventAuditor,
                                    filters: sorted
                                };
                            }
                        }
                    }
                    const index = eventAuditors.findIndex(auditor => auditor.id === eventAuditor.id);
                    updatedEventAuditorArray = [
                        ...eventAuditors.slice(0, index),
                        updatedEventAuditor,
                        ...eventAuditors.slice(index + 1)
                    ];
                    if (filteredEventAuditors) {
                        const filteredIndex = filteredEventAuditors.findIndex(auditor => auditor.id === eventAuditor.id);
                        updatedFilteredEventAuditorArray = [
                            ...filteredEventAuditors.slice(0, filteredIndex),
                            updatedEventAuditor,
                            ...filteredEventAuditors.slice(filteredIndex + 1)
                        ];
                    }
                } 
            } else if (action === "delete") {
                if (eventAuditors) {
                    const deletedIndex = eventAuditors.findIndex(eA => eventAuditor.id === eA.id);
                    updatedEventAuditorArray = [
                        ...eventAuditors.slice(0, deletedIndex),
                        ...eventAuditors.slice(deletedIndex + 1)
                    ];
                    if (filteredEventAuditors) {
                        const filteredIndex = filteredEventAuditors.findIndex(eA => eA.id === eventAuditor.id);
                        updatedFilteredEventAuditorArray = [
                            ...filteredEventAuditors.slice(0, filteredIndex),
                            ...filteredEventAuditors.slice(filteredIndex + 1)
                        ];
                    }
                }
            }
            setEventAuditors(updatedEventAuditorArray);
            setFilteredEventAuditors(updatedFilteredEventAuditorArray);
        }
        setIsLoading(false);
    }

    const checkIfEventAuditorIsDuplicate = (current: EventAuditor) => {
        if (eventAuditors) {
            const foundArray = eventAuditors.filter((ea: EventAuditor) => ea.phoneNumber === current.phoneNumber);
            if (foundArray && foundArray.length > 1) return true;
        }
        return false;
    }

    const filterEventAuditors = (value: string) => {
        const nameResult = eventAuditors ? eventAuditors.filter(ea => (ea.name ? ea.name : "").toLocaleLowerCase().toString().includes(value.toLocaleLowerCase().toString())) : [];
        const phoneNumberResult = eventAuditors ? eventAuditors.filter(ea => (ea.phoneNumber ? ea.phoneNumber : "").toString().includes(value.toString())) : [];
        const result = nameResult.concat(phoneNumberResult);
        setFilteredEventAuditors(result);
    }

    const handleSearchInput = async (value: string) => {
        setSearchText(value);
        if (value) {
            filterEventAuditors(value);
        } else {
            setFilteredEventAuditors(eventAuditors);
        }
    }

    return (
        <>
            {error && <ErrorAlert width="12" error={error} />}
            <IonSearchbar value={searchText} placeholder="Type Name or Phone #" onIonChange={e => handleSearchInput(e.detail.value!)} color="white"></IonSearchbar>
            {isLoading ?
                <Spinner />
                :
                <>
                    {filteredEventAuditors ? 
                        <>
                            {(isWindows() || (isPlatform("desktop"))) ?
                                <Table hover responsive>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Phone Number</th>
                                            <th>Status</th>
                                            <th>Last Updated</th>
                                            {eventFilters?.map((eventFilter, index) => (
                                                <th key={index}>{eventFilter?.name}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEventAuditors.map((eventAuditor, index) => (
                                            <tr key={index} className={checkIfEventAuditorIsDuplicate(eventAuditor) ? "bg-warning" : ""} onClick={() => handleSelectedSubscriber(eventAuditor)}>
                                                <td>
                                                    {index+1}
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{eventAuditor.name}</p>
                                                </td>
                                                <td className="ion-text-wrap">
                                                    {eventAuditor.phoneNumber ?
                                                        <PhoneInput
                                                            placeholder="No phone number found"
                                                            defaultCountry="US"
                                                            disabled={true}
                                                            value={eventAuditor.phoneNumber as any}
                                                            onChange={(phoneNumberValue?: any) => {
                                                                setError("");
                                                            }}
                                                        />
                                                        :
                                                        <p></p>
                                                    }
                                                </td>
                                                <td>
                                                    <span 
                                                        className={(eventAuditor.status === "paused" || eventAuditor.status === "stopped") ? "text-danger" : "text-primary"}
                                                    >
                                                        {eventAuditor.status === "paused" ? "Paused" : eventAuditor.status === "stopped" ? "Stopped" : "Active"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{eventAuditor.updatedOn ? moment(eventAuditor.updatedOn).format("MM/DD/YY h:mm a") : ""}</p>
                                                </td>
                                                {(eventAuditor.filters && eventAuditor.filters.length > 0) ? 
                                                    (eventAuditor.filters?.map((eventAuditorFilter, i) => (
                                                        <td key={i}>
                                                            {(eventAuditorFilter?.options && eventAuditorFilter.options.length > 0) ? 
                                                                <p>
                                                                    {eventAuditorFilter?.options?.map((option, j) => (
                                                                        <span key={j}>
                                                                            {option}{((j+1) < (eventAuditorFilter?.options?.length || 0)) ? ", " : ""}
                                                                        </span>
                                                                    ))}
                                                                </p>
                                                                :
                                                                <p>none</p>
                                                            }   
                                                        </td>
                                                    )))
                                                    :
                                                    <>
                                                        {eventFilters?.map((eventFilter, index) => (
                                                            <th key={index}>*none</th>
                                                        ))}
                                                    </>
                                                }
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            :
                                <IonList className="bg-white">
                                    {filteredEventAuditors.map((eventAuditor, index) => (
                                        <IonItem key={index} onClick={() => handleSelectedSubscriber(eventAuditor)}>
                                            <IonLabel>
                                                <IonRow className={checkIfEventAuditorIsDuplicate(eventAuditor) ? "bg-warning" : ""}>
                                                    <IonCol sizeXs="12" sizeMd="6" className="ion-text-wrap font-weight-bold">
                                                        <h2 className="ion-text-wrap">
                                                            {index+1 + ") " + eventAuditor.name}
                                                        </h2>
                                                    </IonCol>
                                                    <IonCol sizeXs="12" sizeMd="6" className="ion-text-wrap">
                                                        {eventAuditor.phoneNumber ?
                                                            <PhoneInput
                                                                placeholder="No phone number found"
                                                                defaultCountry="US"
                                                                disabled={true}
                                                                value={eventAuditor.phoneNumber as any}
                                                                onChange={(phoneNumberValue?: any) => {
                                                                    setError("");
                                                                }}
                                                            />
                                                            :
                                                            <p></p>
                                                        }
                                                    </IonCol>
                                                    <IonCol sizeXs="12" sizeMd="6" className="ion-text-wrap">
                                                        <p className="ion-text-wrap">
                                                            <span className="font-weight-bold">Status:{" "}</span> 
                                                            <span className={(eventAuditor.status === "paused" || eventAuditor.status === "stopped") ? "text-danger" : "text-primary"}>
                                                                {eventAuditor.status === "paused" ? "Paused" : eventAuditor.status === "stopped" ? "Stopped" : "Active"}
                                                            </span>
                                                        </p>
                                                    </IonCol>
                                                    <IonCol sizeXs="12" sizeMd="6" className="ion-text-wrap">
                                                        <p className="ion-text-wrap">
                                                            <span className="font-weight-bold">Last Updated On:{" "}</span> 
                                                            {eventAuditor.updatedOn ? moment(eventAuditor.updatedOn).format("MM/DD/YY h:mm a") : ""}
                                                        </p>
                                                    </IonCol>
                                                    {eventFilters?.map((eventFilter, i) => (
                                                        <IonCol size="12" className="ion-text-wrap" key={i}>
                                                            {/* For each filter set for this event, create a new Col with the filter name and any options selected for the current eventAuditor */}
                                                            <p className="ion-text-wrap">
                                                                <span className="font-weight-bold">{eventFilter.name}:</span>
                                                                {(eventAuditor.filters && eventAuditor.filters.length > 0) ? 
                                                                    (eventAuditor.filters?.map((eventAuditorFilter, j) => (
                                                                        <>
                                                                            {eventAuditorFilter?.filterId === eventFilter.id && (
                                                                                <>
                                                                                    {/* If there are no options selected for this filter, then the value of eventAuditorFilter will be null */}
                                                                                    <IonCol sizeXs="12" sizeMd="6" className="ion-text-wrap" key={j}>
                                                                                        {(eventAuditorFilter?.options && eventAuditorFilter.options.length > 0) ? 
                                                                                            <span>
                                                                                                {eventAuditorFilter?.options?.map((option, k) => (
                                                                                                    <span key={k}>
                                                                                                        {option}{((k+1) < (eventAuditorFilter?.options?.length || 0)) ? ", " : ""}
                                                                                                    </span>
                                                                                                ))}
                                                                                            </span>
                                                                                            :
                                                                                            // Covers the case where the EventAuditor has a matched filter, but no options were selected
                                                                                            <span>None</span>
                                                                                        }   
                                                                                    </IonCol>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )))
                                                                    :
                                                                    //Covers the case where there are zero filters associated with this EventAuditor
                                                                   <p>None</p>
                                                                }
                                                            </p>
                                                        </IonCol>
                                                    ))}
                                                </IonRow>
                                            </IonLabel>
                                        </IonItem>
                                    ))}
                                </IonList>
                            }
                        </>
                        
                        :
                        <p>No subscribers yet.</p>
                    }
                </>
            }
            <EventAuditorModal 
                show={showModal} 
                eventAuditor={selectedEventAuditor} 
                isAdminView={true} event={event} 
                onChange={(action: ("create" | "update" | "delete" | "close"), eventAuditor: EventAuditor) => handleEventAuditorChange(action, eventAuditor)} 
            />
        </>
    );
};

export default EditEventAuditorsFullTable;
